/**
 * Highlights matched terms in text/markdown by wrapping them in styled spans.
 * Works with react-markdown + rehype-raw since they allow inline HTML.
 */

/** Amber used by every "search-result" highlight (headlines body, filter menu, etc.). */
export const SEARCH_HIGHLIGHT_COLOR = "#FBBF24";

/**
 * Highlights terms in a markdown/text string by wrapping matches in styled spans.
 * Uses case-insensitive matching which works for both tickers and keywords.
 *
 * @param text - The text/markdown content to process
 * @param terms - Terms to highlight (tickers and/or keywords)
 * @param color - Background color for highlights
 * @param wordBoundary - When true (default), only match whole "words" (tickers / keywords).
 *   Pass false for free-text substring search (e.g. typing "mag" should highlight "Mag" in "Magnificent 7").
 * @returns The text with matched terms wrapped in styled spans
 */
export function highlightTermsInText(
  text: string | null | undefined,
  terms: string[],
  color: string,
  wordBoundary: boolean = true
): string {
  if (!text || terms.length === 0) return text || "";

  const regex = createHighlightRegex(terms, true, wordBoundary);
  return text.replace(
    regex,
    `<span style="background-color: ${color}; color: black; padding: 0 2px; border-radius: 2px;">$1</span>`
  );
}

/**
 * Creates a regex for matching with global flag for replacement.
 * Uses negative lookbehind/lookahead for proper word boundaries when requested.
 */
function createHighlightRegex(
  terms: string[],
  caseInsensitive: boolean,
  wordBoundary: boolean = true
): RegExp {
  const escapedTerms = terms.map((term) =>
    term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  const flags = caseInsensitive ? "gi" : "g";
  const group = `(${escapedTerms.join("|")})`;
  const pattern = wordBoundary
    ? `(?<![a-zA-Z0-9_])${group}(?![a-zA-Z0-9_])`
    : group;
  return new RegExp(pattern, flags);
}
